import React, { useEffect, useMemo, useRef, useState } from "react";
import { ScrollView, StyleSheet, Text, TouchableOpacity, View } from "react-native";
import { v2Styles as sharedStyles } from "./LocationSummaryV2.styles";
import SkeletonBlock from "../../../components/SkeletonBlock";
import {
  ASNLocationDetailsRow,
  AsnLocationGrainKey,
  ASNLocationDetailsCompanionRow,
  ASNLocationDetailsCompanionGrandTotal,
  ASNLocationDetailsCompanionPagination,
  ASNLocationDetailsCompanionRequest,
  fetchAsnLocationDetailsCompanion,
} from "../api/asnOverviewKpiApi";
import {
  buildAsnLocationDetailsRequest,
  AsnOverviewKpiRequestInputs,
} from "../utils/asnOverviewKpiRequestBuilder";
import { formatMetric } from "./LocationSummaryV2MockData";
import LocationSummaryPagination from "./LocationSummaryPagination";
import { PAGE_SIZES } from "./LocationSummaryV2Table.shared";

// ─── Shared column width model ───────────────────────────────────────────────
// Each column defines a minimum width plus a growth weight. When extra space
// is available, we distribute only the remaining width by grow weight.
type CompanionColumnKey = "location" | "region" | "market" | CompanionMetricKey;

const TABLE_SIDE_PADDING = 20;
const CELL_HORIZONTAL_PADDING = 10;

const COL_LAYOUT: Record<CompanionColumnKey, { minWidth: number; grow: number }> = {
  location: { minWidth: 300, grow: 2.1 },
  region: { minWidth: 170, grow: 1.2 },
  market: { minWidth: 170, grow: 1.2 },
  nonAsnLoads: { minWidth: 110, grow: 0.85 },
  totalLoads: { minWidth: 110, grow: 0.85 },
  pctNonAsn: { minWidth: 110, grow: 0.85 },
  avgSkus: { minWidth: 110, grow: 0.85 },
  caseQty: { minWidth: 110, grow: 0.85 },
};

const TABLE_MIN_CONTENT_WIDTH = Object.values(COL_LAYOUT).reduce(
  (sum, col) => sum + col.minWidth,
  0
);

const TOTAL_GROW = Object.values(COL_LAYOUT).reduce(
  (sum, col) => sum + col.grow,
  0
);

// Companion metric columns in mock order. pyPct is intentionally excluded —
// it does not appear in the mock companion table.
const COMPANION_METRIC_KEYS = [
  "nonAsnLoads",
  "totalLoads",
  "pctNonAsn",
  "avgSkus",
  "caseQty",
] as const;

type CompanionMetricKey = typeof COMPANION_METRIC_KEYS[number];

const COMPANION_METRIC_LABELS: Record<CompanionMetricKey, string> = {
  nonAsnLoads: "Non-ASN Loads",
  totalLoads: "Total Loads",
  pctNonAsn: "% Non-ASN",
  avgSkus: "Avg SKUs",
  caseQty: "Case Qty",
};

// ─── Data Adapter ─────────────────────────────────────────────────────────────
// The API row itself carries no `pctNonAsn` — it's derived here from
// nonAsnLoads/totalLoads so the rendering code has a single display shape.
type CompanionDisplayRow = ASNLocationDetailsCompanionRow & { pctNonAsn: number };

/**
 * Coerce nullish metric values to 0 at the API boundary.
 *
 * The API returns flat rows — metrics are direct fields on the row object, not
 * nested inside a `vals` object. Coercing to 0 here prevents formatMetric from
 * receiving undefined/null values.
 */
function adaptCompanionRows(
  rows: ASNLocationDetailsCompanionRow[] | undefined
): CompanionDisplayRow[] {
  if (!rows || !Array.isArray(rows)) {
    return [];
  }

  return rows
    .filter((row): row is ASNLocationDetailsCompanionRow => row != null)
    .map((row) => {
      const nonAsnLoads = row.nonAsnLoads ?? 0;
      const totalLoads = row.totalLoads ?? 0;
      return {
        ...row,
        nonAsnLoads,
        totalLoads,
        avgSkus: row.avgSkus ?? 0,
        caseQty: row.caseQty ?? 0,
        pctNonAsn: totalLoads > 0 ? (nonAsnLoads / totalLoads) * 100 : 0,
      };
    });
}

// ─── Geographic grain keys (for scopeKeys extraction) ─────────────────────────
const GEO_GRAIN_KEYS: AsnLocationGrainKey[] = ["region", "market", "location"];

// ─── Props ────────────────────────────────────────────────────────────────────

interface Props {
  perspective: "shipping" | "receiving";
  /** Undefined when no row is selected yet (or after "Clear") — renders a
   * persistent placeholder instead of unmounting the whole companion table. */
  anchorRow: ASNLocationDetailsRow | undefined;
  effectiveGrainKeys: AsnLocationGrainKey[];
  filterInputs: AsnOverviewKpiRequestInputs;
  onClear: () => void;
}

// ─── CompanionSkeletonRows ───────────────────────────────────────────────────
// Shimmer placeholder matching the companion table's column layout.
// Rendered inside the same horizontal ScrollView as the real data so column
// widths stay identical and no layout shift occurs when data arrives.
// Uses SkeletonBlock — the shared single-block skeleton primitive — composed
// into rows matching the companion table's 3 dim + 5 metric column structure.
const SKELETON_ROW_COUNT = 4;

interface CompanionSkeletonRowsProps {
  getColumnStyle: (key: CompanionColumnKey) => { width: number };
}

const CompanionSkeletonRows: React.FC<CompanionSkeletonRowsProps> = ({ getColumnStyle }) => (
  <>
    {Array.from({ length: SKELETON_ROW_COUNT }).map((_, i) => (
      <View key={i} style={localStyles.dataRow}>
        {/* 3 dimension columns */}
        <View style={[localStyles.colDim, getColumnStyle("location")]}>
          <SkeletonBlock style={{ height: 12, width: "85%", borderRadius: 6 }} />
        </View>
        <View style={[localStyles.colDim, getColumnStyle("region")]}>
          <SkeletonBlock style={{ height: 12, width: "70%", borderRadius: 6 }} />
        </View>
        <View style={[localStyles.colDim, getColumnStyle("market")]}>
          <SkeletonBlock style={{ height: 12, width: "75%", borderRadius: 6 }} />
        </View>
        {/* 5 metric columns */}
        {COMPANION_METRIC_KEYS.map((key) => (
          <View
            key={key}
            style={[localStyles.colMetric, getColumnStyle(key), { alignItems: "flex-end" }]}
          >
            <SkeletonBlock style={{ height: 12, width: 52, borderRadius: 6 }} />
          </View>
        ))}
      </View>
    ))}
  </>
);

// ─── Component ────────────────────────────────────────────────────────────────

const LocationSummaryV2CompanionTable: React.FC<Props> = ({
  perspective,
  anchorRow,
  effectiveGrainKeys,
  filterInputs,
  onClear,
}) => {
  const [rows, setRows] = useState<CompanionDisplayRow[]>([]);
  const [grandTotal, setGrandTotal] = useState<ASNLocationDetailsCompanionGrandTotal | null>(null);
  const [serverPagination, setServerPagination] = useState<ASNLocationDetailsCompanionPagination | null>(null);
  const [sortBy, setSortBy] = useState("nonAsnLoads");
  const [sortDesc, setSortDesc] = useState(true);
  const [pageNumber, setPageNumber] = useState(0);
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZES[0]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  // Prefer the backend's aggregate; fall back to a client-side sum only if
  // the response ever omits it (e.g. an older API version).
  const totals = useMemo<ASNLocationDetailsCompanionGrandTotal>(() => {
    if (grandTotal) return grandTotal;
    const sums = rows.reduce(
      (acc, row) => {
        acc.nonAsnLoads += row.nonAsnLoads;
        acc.totalLoads += row.totalLoads;
        acc.caseQty += row.caseQty;
        acc.avgSkusSum += row.avgSkus;
        return acc;
      },
      { nonAsnLoads: 0, totalLoads: 0, caseQty: 0, avgSkusSum: 0 }
    );
    return {
      locationCount: rows.length,
      nonAsnLoads: sums.nonAsnLoads,
      totalLoads: sums.totalLoads,
      pctNonAsn: sums.totalLoads > 0 ? (sums.nonAsnLoads / sums.totalLoads) * 100 : 0,
      avgSkus: rows.length > 0 ? sums.avgSkusSum / rows.length : 0,
      caseQty: sums.caseQty,
    };
  }, [grandTotal, rows]);

  const tableWidth = useMemo(
    () => Math.max(containerWidth, TABLE_MIN_CONTENT_WIDTH + TABLE_SIDE_PADDING * 2),
    [containerWidth]
  );
  const contentWidth = useMemo(
    () => Math.max(tableWidth - TABLE_SIDE_PADDING * 2, TABLE_MIN_CONTENT_WIDTH),
    [tableWidth]
  );
  const hasHorizontalOverflow = tableWidth > containerWidth + 0.5;

  const getColumnStyle = useMemo(() => {
    const extraWidth = Math.max(0, contentWidth - TABLE_MIN_CONTENT_WIDTH);

    return (key: CompanionColumnKey) => ({
      width:
        COL_LAYOUT[key].minWidth +
        (extraWidth * COL_LAYOUT[key].grow) / TOTAL_GROW,
    });
  }, [contentWidth]);

  // Stale-response guard — discard responses from superseded requests.
  const requestIdRef = useRef(0);
  // Tracks the previously fetched anchor so a genuinely new selection resets
  // to page 0, without waiting an extra render for the setPageNumber to land.
  const prevAnchorKeyRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!anchorRow) {
      // No row selected (initial state, or after "Clear") — nothing to fetch;
      // invalidate any in-flight request so a late response can't repopulate rows.
      requestIdRef.current += 1;
      prevAnchorKeyRef.current = undefined;
      setRows([]);
      setGrandTotal(null);
      setServerPagination(null);
      setError(null);
      setLoading(false);
      return;
    }

    const anchorChanged = prevAnchorKeyRef.current !== anchorRow.key;
    prevAnchorKeyRef.current = anchorRow.key;
    const effectivePageNumber = anchorChanged ? 0 : pageNumber;
    if (anchorChanged && pageNumber !== 0) setPageNumber(0);

    // scopeKeys = the geo grains actually present on the anchor itself (not
    // effectiveGrainKeys) so a region/market-only anchor never claims a
    // "location" scope it has no value for.
    const scopeKeys = (Object.keys(anchorRow.grainVals) as AsnLocationGrainKey[]).filter(
      (k) => GEO_GRAIN_KEYS.includes(k)
    );

    // Build the base filter fields from the shared filter bar inputs.
    const baseRequest = buildAsnLocationDetailsRequest({
      ...filterInputs,
      perspective,
      grainSelections: effectiveGrainKeys,
      sortBy,
      sortDirection: sortDesc ? "DESC" : "ASC",
      searchQuery: "",
      pageNumber: effectivePageNumber,
      pageSize,
    });

    const companionRequest: ASNLocationDetailsCompanionRequest = {
      shippingRegions: baseRequest.shippingRegions,
      shippingMarkets: baseRequest.shippingMarkets,
      shippingLocationIds: baseRequest.shippingLocationIds,
      receivingLocationIds: baseRequest.receivingLocationIds,
      years: baseRequest.years,
      periods: baseRequest.periods,
      weeks: baseRequest.weeks,
      dateFrom: baseRequest.dateFrom,
      dateTo: baseRequest.dateTo,
      ...(baseRequest.caseQuantityMin !== undefined
        ? { caseQuantityMin: baseRequest.caseQuantityMin }
        : {}),
      ...(baseRequest.caseQuantityMax !== undefined
        ? { caseQuantityMax: baseRequest.caseQuantityMax }
        : {}),
      perspective,
      scopeKeys,
      scopeVals: anchorRow.grainVals,
      sortBy,
      sortDirection: sortDesc ? "DESC" : "ASC",
      pageNumber: effectivePageNumber,
      // Request a generous batch regardless of the display page size — the
      // backend doesn't paginate this endpoint yet, so pagination below is
      // done client-side over the full set; this auto-upgrades to true
      // server pagination the moment the response carries `totalPages`.
      pageSize: 200,
    };

    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(null);

    fetchAsnLocationDetailsCompanion(companionRequest)
      .then((response) => {
        if (requestId !== requestIdRef.current) return; // stale — discard
        setRows(adaptCompanionRows(response.rows));
        setGrandTotal(response.grandTotal ?? null);
        setServerPagination(
          typeof response.pagination?.totalPages === "number" ? response.pagination : null
        );
        setLoading(false);
      })
      .catch((err: any) => {
        if (requestId !== requestIdRef.current) return; // stale — discard
        const msg =
          err?.response?.data?.message ??
          err?.message ??
          "An unexpected error occurred loading linked locations.";
        setError(msg);
        setLoading(false);
      });
    // Re-fetch whenever the anchor row, perspective, filters, sort, or page change.
  }, [anchorRow?.key, perspective, filterInputs, sortBy, sortDesc, pageNumber, pageSize]);

  const side = perspective === "shipping" ? "Receiving" : "Shipping";

  // Server pagination if the backend ever returns it (see
  // ASNLocationDetailsCompanionPagination), else client-side slice over the
  // full fetched batch — same fallback pattern as the main location table.
  const totalRows = serverPagination?.totalRowCount ?? rows.length;
  const currentPage = serverPagination?.pageIndex ?? pageNumber;
  const currentPageSize = serverPagination?.pageSize ?? pageSize;
  const pages = serverPagination
    ? Math.max(1, serverPagination.totalPages as number)
    : Math.max(1, Math.ceil(rows.length / pageSize));
  const pageRows = serverPagination
    ? rows
    : rows.slice(pageNumber * pageSize, (pageNumber + 1) * pageSize);

  const handleCompanionSort = (key: string) => {
    setSortDesc(sortBy === key ? !sortDesc : false);
    setSortBy(key);
    setPageNumber(0);
  };

  if (!anchorRow) {
    return (
      <View style={[sharedStyles.card, localStyles.cardFullWidth]} testID="asn-v2-companion-table">
        <View style={sharedStyles.companionHeader}>
          <Text style={sharedStyles.companionIcon}>
            {perspective === "shipping" ? "↙" : "↗"}
          </Text>
          <View style={{ flex: 1 }}>
            <Text style={sharedStyles.companionTitle}>{side} Locations</Text>
            <Text style={sharedStyles.companionSub}>
              Select a row in the table above to reveal linked {side.toLowerCase()} locations here.
            </Text>
          </View>
          <TouchableOpacity
            style={sharedStyles.secondaryButton}
            onPress={onClear}
            testID="asn-v2-companion-clear"
          >
            <Text style={sharedStyles.secondaryText}>Clear</Text>
          </TouchableOpacity>
        </View>
        <View style={localStyles.noAnchorBand}>
          <Text style={localStyles.noAnchorText}>
            Click any row above to load {side.toLowerCase()} counterpart locations.
          </Text>
        </View>
      </View>
    );
  }

  // Derive a display name for the anchor from its grain values, preferring the
  // most specific grain available (location > market > region).
  const anchorLabel =
    anchorRow.grainVals.location ??
    anchorRow.grainVals.market ??
    anchorRow.grainVals.region ??
    anchorRow.key;

  return (
    <View style={[sharedStyles.card, localStyles.cardFullWidth]} testID="asn-v2-companion-table">
      <View style={sharedStyles.companionHeader}>
        <Text style={sharedStyles.companionIcon}>
          {perspective === "shipping" ? "↙" : "↗"}
        </Text>
        <View style={{ flex: 1 }}>
          <Text style={sharedStyles.companionTitle}>
            {side} locations linked to "{anchorLabel}"
          </Text>
          <Text style={sharedStyles.companionSub}>
            These {side.toLowerCase()} sites exchanged loads with the selected {perspective} scope in the current filter range.
          </Text>
        </View>
        <TouchableOpacity
          style={sharedStyles.secondaryButton}
          onPress={onClear}
          testID="asn-v2-companion-clear"
        >
          <Text style={sharedStyles.secondaryText}>Clear</Text>
        </TouchableOpacity>
      </View>

      {!loading && error && (
        <Text style={[sharedStyles.footerText, { paddingHorizontal: 12, paddingVertical: 8, color: "#C0392B" }]}>
          {error}
        </Text>
      )}

      {/* Single horizontal scroll context — header and body share one View
          so widths are resolved once and the scroll thumb covers both. */}
      {!error && (
        <View
          style={localStyles.tableViewport}
          onLayout={(event) => setContainerWidth(event.nativeEvent.layout.width)}
        >
          <ScrollView
            horizontal
            scrollEnabled={hasHorizontalOverflow}
            showsHorizontalScrollIndicator={hasHorizontalOverflow}
            style={localStyles.scrollView}
            contentContainerStyle={localStyles.scrollContent}
          >
            <View style={[localStyles.table, { width: tableWidth }]}>
              {/* ── Header row ── */}
              <View style={localStyles.headerRow}>
                {/* Dimension header cells */}
                {(["location", "region", "market"] as const).map((key) => (
                  <TouchableOpacity
                    key={key}
                    style={[localStyles.colDim, getColumnStyle(key), localStyles.headerCellWrap]}
                    onPress={() => handleCompanionSort(key)}
                    testID={`asn-v2-companion-sort-${key}`}
                  >
                    <Text
                      style={[localStyles.headerCellText, localStyles.headerCellDim]}
                      numberOfLines={1}
                    >
                      {key === "location"
                        ? `${side} Location`
                        : key === "region"
                        ? "Region / Unit"
                        : "Market / POD"}
                      {sortBy === key ? (sortDesc ? " ↓" : " ↑") : ""}
                    </Text>
                  </TouchableOpacity>
                ))}
                {/* Metric header cells */}
                {COMPANION_METRIC_KEYS.map((key) => (
                  <TouchableOpacity
                    key={key}
                    style={[localStyles.colMetric, getColumnStyle(key), localStyles.headerCellWrap]}
                    onPress={() => handleCompanionSort(key)}
                    testID={`asn-v2-companion-sort-${key}`}
                  >
                    <Text
                      style={[localStyles.headerCellText, localStyles.headerCellNum]}
                      numberOfLines={1}
                    >
                      {COMPANION_METRIC_LABELS[key]}
                      {sortBy === key ? (sortDesc ? " ↓" : " ↑") : ""}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>

              {/* ── Skeleton rows shown while loading ── */}
              {loading && <CompanionSkeletonRows getColumnStyle={getColumnStyle} />}

              {/* ── Total row — always shown once loaded, mirrors the main table's Total row ── */}
              {!loading && (
                <View style={localStyles.dataRow}>
                  <View style={[localStyles.colDim, getColumnStyle("location")]}>
                    <Text style={[localStyles.cellText, localStyles.cellDim, localStyles.totalText]} numberOfLines={1}>
                      {`Total — ${totals.locationCount} ${side.toLowerCase()} location${totals.locationCount !== 1 ? "s" : ""}`}
                    </Text>
                  </View>
                  <View style={[localStyles.colDim, getColumnStyle("region")]} />
                  <View style={[localStyles.colDim, getColumnStyle("market")]} />
                  {COMPANION_METRIC_KEYS.map((key) => (
                    <View key={key} style={[localStyles.colMetric, getColumnStyle(key)]}>
                      <Text style={[localStyles.cellText, localStyles.cellNum, localStyles.totalText]}>
                        {formatMetric(totals[key], key as Parameters<typeof formatMetric>[1])}
                      </Text>
                    </View>
                  ))}
                </View>
              )}

              {/* ── Data rows ── */}
              {!loading && pageRows.map((row) => (
                <View key={row.location} style={localStyles.dataRow}>
                  {/* Location */}
                  <View style={[localStyles.colDim, getColumnStyle("location")]}>
                    <Text style={[localStyles.cellText, localStyles.cellDim, localStyles.locationText, sharedStyles.good]}>
                      {row.location}
                    </Text>
                  </View>
                  {/* Region */}
                  <View style={[localStyles.colDim, getColumnStyle("region")]}>
                    <Text style={[localStyles.cellText, localStyles.cellDim]} numberOfLines={1}>
                      {row.region}
                    </Text>
                  </View>
                  {/* Market / POD */}
                  <View style={[localStyles.colDim, getColumnStyle("market")]}>
                    <Text style={[localStyles.cellText, localStyles.cellDim]} numberOfLines={1}>
                      {row.market}
                    </Text>
                  </View>
                  {/* Metrics — read directly from the flat row shape */}
                  {COMPANION_METRIC_KEYS.map((key) => {
                    const rawVal = row[key] ?? 0;
                    return (
                      <View key={key} style={[localStyles.colMetric, getColumnStyle(key)]}>
                        <Text
                          style={[
                            localStyles.cellText,
                            localStyles.cellNum,
                            key === "pctNonAsn" &&
                              (rawVal > 25 ? sharedStyles.danger : sharedStyles.good),
                          ]}
                        >
                          {formatMetric(rawVal, key as Parameters<typeof formatMetric>[1])}
                        </Text>
                      </View>
                    );
                  })}
                </View>
              ))}

              {!loading && pageRows.length === 0 && (
                <View style={localStyles.dataRow}>
                  <Text style={[localStyles.cellText, localStyles.cellDim, { paddingHorizontal: 8 }]}> 
                    No linked {side.toLowerCase()} locations found.
                  </Text>
                </View>
              )}
            </View>
          </ScrollView>
        </View>
      )}

      {!loading && !error && (
        <LocationSummaryPagination
          totalRows={totalRows}
          pageSize={currentPageSize}
          page={currentPage}
          pages={pages}
          onPageSizeChange={(size) => { setPageSize(size); setPageNumber(0); }}
          onPrev={() => setPageNumber((p) => Math.max(0, p - 1))}
          onNext={() => setPageNumber((p) => Math.min(pages - 1, p + 1))}
          onFirst={() => setPageNumber(0)}
          onLast={() => setPageNumber(Math.max(0, pages - 1))}
        />
      )}

      <View style={sharedStyles.footer}>
        <Text style={sharedStyles.footerText}>
          {loading
            ? "Loading linked locations…"
            : `${totalRows} ${side.toLowerCase()} location${totalRows !== 1 ? "s" : ""} linked to the selected ${perspective} scope`}
        </Text>
      </View>
    </View>
  );
};

// ─── Component-local styles ───────────────────────────────────────────────────
// These styles live here (not in v2Styles) because they are specific to the
// companion table's fixed-width column layout. All column-sizing decisions are
// made once in COL and referenced here.
const localStyles = StyleSheet.create({
  cardFullWidth: {
    width: "100%",
    alignSelf: "stretch",
  },
  tableViewport: {
    width: "100%",
    alignSelf: "stretch",
  },
  // scrollView: the ScrollView itself must fill the card's full width so the
  // header and footer align flush with the card edges.
  scrollView: {
    width: "100%",
  },
  // scrollContent: flexGrow:1 expands horizontally when content overflows.
  // minWidth:"100%" ensures the content area is never narrower than the
  // ScrollView frame itself, so the table fills the full card width when
  // total column width is less than the available screen width.
  scrollContent: {
    minWidth: "100%",
  },
  table: {
    minWidth: "100%",
  },
  headerRow: {
    flexDirection: "row",
    backgroundColor: "#F5F8FC",
    minHeight: 44,
    alignItems: "center",
    borderBottomWidth: 2,
    borderBottomColor: "#D7E5F8",
    paddingHorizontal: TABLE_SIDE_PADDING,
  },
  dataRow: {
    flexDirection: "row",
    minHeight: 44,
    alignItems: "stretch",
    borderBottomWidth: 1,
    borderBottomColor: "#EDF0F4",
    paddingHorizontal: TABLE_SIDE_PADDING,
  },
  // ── Column containers (shared by header and body) ─────────────────────────
  // Layout properties only — no textAlign, no typography.
  colDim: {
    paddingHorizontal: CELL_HORIZONTAL_PADDING,
    paddingVertical: 4,
    justifyContent: "center",
    flexShrink: 0,
  },
  colMetric: {
    paddingHorizontal: CELL_HORIZONTAL_PADDING,
    paddingVertical: 4,
    alignItems: "flex-end",
    justifyContent: "center",
    flexShrink: 0,
  },
  // Header wrapper adds nothing to sizing; kept for semantic clarity.
  headerCellWrap: {},
  // ── Header text ───────────────────────────────────────────────────────────
  headerCellText: {
    fontSize: 11,
    fontWeight: "700",
    color: "#56606C",
    textTransform: "uppercase",
  },
  headerCellDim: {
    textAlign: "left",
  },
  headerCellNum: {
    textAlign: "right",
  },
  // ── Body cell text ────────────────────────────────────────────────────────
  cellText: {
    fontSize: 12,
    color: "#28313B",
  },
  cellDim: {
    textAlign: "left",
  },
  cellNum: {
    textAlign: "right",
    fontVariant: ["tabular-nums"],
  },
  locationText: {
    flexShrink: 1,
    flexWrap: "wrap",
  },
  totalText: {
    fontWeight: "700",
  },
  // ── Placeholder shown when no row is selected yet (or after "Clear") ──────
  noAnchorBand: {
    backgroundColor: "#EEF3FB",
    paddingVertical: 28,
    paddingHorizontal: 20,
    alignItems: "center",
    justifyContent: "center",
  },
  noAnchorText: {
    fontSize: 13,
    fontStyle: "italic",
    color: "#808995",
    textAlign: "center",
  },
});

export default LocationSummaryV2CompanionTable;
